import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, MessageSquare, Send, Copy, Save, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "geral", label: "Geral" },
  { value: "lembrete", label: "Lembrete de horário" },
  { value: "reativacao", label: "Reativação" },
  { value: "aniversario", label: "Aniversário" },
  { value: "promocao", label: "Promoção" },
  { value: "agradecimento", label: "Agradecimento" },
];

export default function Messages() {
  const { apiCall } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "geral", content: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState(null);
  const [bulkSearch, setBulkSearch] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [rendered, setRendered] = useState([]);
  const [bulkStep, setBulkStep] = useState(0);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes, cRes] = await Promise.all([
        apiCall("get", "/message-templates"),
        apiCall("get", "/message-templates/placeholders"),
        apiCall("get", "/clients"),
      ]);
      setTemplates(tRes.data || []);
      setPlaceholders(pRes.data || []);
      setClients(cRes.data || []);
    } catch (e) {
      toast.error("Erro ao carregar central de mensagens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("Nome e conteúdo são obrigatórios");
      return;
    }
    try {
      if (editing) {
        await apiCall("put", `/message-templates/${editing}`, form);
        toast.success("Modelo atualizado");
      } else {
        await apiCall("post", "/message-templates", form);
        toast.success("Modelo criado");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", category: "geral", content: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const handleEdit = (t) => {
    setEditing(t.template_id);
    setForm({ name: t.name, category: t.category || "geral", content: t.content });
    setOpen(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Excluir modelo "${t.name}"?`)) return;
    try {
      await apiCall("delete", `/message-templates/${t.template_id}`);
      toast.success("Modelo removido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const insertPlaceholder = (key) => {
    setForm((f) => ({ ...f, content: f.content + key }));
  };

  const openBulk = (t) => {
    setBulkTemplate(t);
    setSelectedClientIds([]);
    setRendered([]);
    setBulkStep(0);
    setBulkSearch("");
    setBulkOpen(true);
  };

  const filteredClients = useMemo(() => {
    if (!bulkSearch.trim()) return clients;
    const q = bulkSearch.toLowerCase();
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, bulkSearch]);

  const toggleClient = (id) => {
    setSelectedClientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    const ids = filteredClients.map((c) => c.client_id);
    const allSelected = ids.every((id) => selectedClientIds.includes(id));
    if (allSelected) {
      setSelectedClientIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedClientIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  const startBulkSend = async () => {
    if (selectedClientIds.length === 0) {
      toast.error("Selecione ao menos 1 cliente");
      return;
    }
    setSending(true);
    try {
      const res = await apiCall("post", "/message-templates/render-bulk", {
        template_id: bulkTemplate.template_id,
        client_ids: selectedClientIds,
      });
      setRendered(res.data || []);
      setBulkStep(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao preparar mensagens");
    } finally {
      setSending(false);
    }
  };

  const currentMsg = rendered[bulkStep];
  const remaining = rendered.length - bulkStep - 1;

  const handleSendCurrent = () => {
    if (!currentMsg?.wa_link) {
      toast.error("Este cliente não tem WhatsApp cadastrado");
      return;
    }
    window.open(currentMsg.wa_link, "_blank");
  };

  const nextOne = () => {
    setBulkStep((s) => Math.min(s + 1, rendered.length - 1));
  };

  return (
    <div className="space-y-4" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Marketing</div>
          <h1 className="text-2xl font-light tracking-tight">Central de Mensagens</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Crie modelos com variáveis (ex: <code className="bg-secondary px-1 rounded">{"{primeiro_nome}"}</code>) e dispare em sequência via WhatsApp.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setForm({ name: "", category: "geral", content: "" });
            setOpen(true);
          }}
          data-testid="new-template-btn"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />
          Novo modelo
        </Button>
      </div>

      {loading ? (
        <Card className="p-6 text-center text-muted-foreground">Carregando…</Card>
      ) : templates.length === 0 ? (
        <Card className="p-8 text-center" data-testid="templates-empty">
          <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Nenhum modelo criado ainda.</p>
          <p className="text-xs text-muted-foreground mt-1">Crie um modelo para começar a enviar mensagens em massa.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((t) => (
            <Card key={t.template_id} className="p-4 hover:shadow-md transition" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {CATEGORIES.find((c) => c.value === t.category)?.label || t.category}
                  </div>
                  <h3 className="font-medium truncate">{t.name}</h3>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(t)} data-testid={`edit-template-${t.template_id}`}>
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(t)} data-testid={`delete-template-${t.template_id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-4 mb-3">{t.content}</p>
              <Button size="sm" className="w-full" onClick={() => openBulk(t)} data-testid={`bulk-send-${t.template_id}`}>
                <Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                Enviar em massa
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* CRUD Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              {editing ? "Editar modelo" : "Novo modelo"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Lembrete 30 dias" data-testid="template-name-input" />
              </div>
              <div>
                <Label className="text-xs">Categoria</Label>
                <select className="w-full h-9 rounded-md border border-input px-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="template-category-select">
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Variáveis disponíveis (clique para inserir)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => insertPlaceholder(p.key)}
                    className="text-[11px] px-2 py-1 rounded-full bg-secondary hover:bg-amber-100 border border-border transition"
                    title={p.label}
                    data-testid={`placeholder-${p.key}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Mensagem</Label>
              <Textarea
                rows={6}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Oi {primeiro_nome}! Que tal agendar?..."
                data-testid="template-content-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                As variáveis serão substituídas pelos dados de cada cliente no momento do envio.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="template-cancel">Cancelar</Button>
              <Button onClick={handleSave} data-testid="template-save"><Save className="h-4 w-4 mr-1" strokeWidth={1.5} />Salvar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk send dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              Envio em massa — {bulkTemplate?.name}
            </DialogTitle>
          </DialogHeader>

          {rendered.length === 0 ? (
            <div className="space-y-3 mt-2">
              <Input
                placeholder="Buscar cliente por nome…"
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
                data-testid="bulk-search-input"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {selectedClientIds.length} selecionados · {filteredClients.length} visíveis
                </span>
                <Button size="sm" variant="ghost" onClick={toggleAllVisible} data-testid="bulk-toggle-all">
                  Marcar/desmarcar todos visíveis
                </Button>
              </div>
              <div className="border border-border rounded-md max-h-[40vh] overflow-y-auto divide-y divide-border">
                {filteredClients.slice(0, 500).map((c) => (
                  <label
                    key={c.client_id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-secondary/40 cursor-pointer"
                    data-testid={`bulk-client-${c.client_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(c.client_id)}
                      onChange={() => toggleClient(c.client_id)}
                    />
                    <span className="text-sm flex-1">{c.name}</span>
                    <span className="text-[10px] text-muted-foreground">{c.phone || "sem telefone"}</span>
                  </label>
                ))}
                {filteredClients.length > 500 && (
                  <div className="px-3 py-2 text-[10px] text-muted-foreground">
                    Mostrando os 500 primeiros — refine a busca para encontrar outros.
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setBulkOpen(false)}>Cancelar</Button>
                <Button onClick={startBulkSend} disabled={sending || selectedClientIds.length === 0} data-testid="bulk-prepare-btn">
                  {sending ? "Preparando…" : `Preparar mensagens (${selectedClientIds.length})`}
                </Button>
              </div>
            </div>
          ) : currentMsg ? (
            <div className="space-y-3 mt-2" data-testid="bulk-step">
              <div className="flex items-center justify-between text-xs">
                <span>
                  Cliente {bulkStep + 1} de {rendered.length}
                  {remaining > 0 ? ` · ${remaining} restante${remaining > 1 ? "s" : ""}` : " · último"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => { setRendered([]); setBulkStep(0); }} data-testid="bulk-back">
                  <X className="h-3 w-3 mr-1" strokeWidth={1.5} /> Voltar
                </Button>
              </div>
              <Card className="p-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Para</div>
                <div className="font-medium" data-testid="bulk-current-name">{currentMsg.name} <span className="text-xs text-muted-foreground">· {currentMsg.phone || "sem telefone"}</span></div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-3 mb-1">Mensagem</div>
                <p className="text-sm whitespace-pre-wrap p-3 bg-secondary/30 rounded-md" data-testid="bulk-current-message">{currentMsg.message}</p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { navigator.clipboard?.writeText(currentMsg.message); toast.success("Copiado"); }}
                    data-testid="bulk-copy"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Copiar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSendCurrent}
                    disabled={!currentMsg.wa_link}
                    data-testid="bulk-send-current"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Abrir WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={nextOne}
                    disabled={remaining === 0}
                    data-testid="bulk-next"
                  >
                    Próximo <ChevronRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                  </Button>
                </div>
              </Card>
              <div className="flex gap-1 flex-wrap mt-2">
                {rendered.map((r, idx) => (
                  <button
                    key={r.client_id}
                    type="button"
                    onClick={() => setBulkStep(idx)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${idx === bulkStep ? "bg-foreground text-background" : idx < bulkStep ? "opacity-50" : ""}`}
                    title={r.name}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
